/**
 * Created by Simon Zhao on 7/16/2021.
 * Description:
 * This component serves as the container for template setting's adminstration.
 * ------ maintenance history ------
 */

import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { createSelector, select, Store } from '@ngrx/store';
import { ColDef, Grid, GridApi, GridOptions } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { AppState } from '../../../../redux';
import { MDLTemplateActionTypes } from '../../../../redux/actions/mdl-template.actions';
import { fullEntityTypeSelector } from '../../../../redux/reducers/entity-type.reducer';
import { fullEntryTypeSelector } from '../../../../redux/reducers/entry-type.reducer';
import { allMDLTemplatesSelector } from '../../../../redux/reducers/mdl-template.reducer';
import { EntityType } from '../../../../tamalelibs/models/entity-type.model';
import { EntryType } from '../../../../tamalelibs/models/entry-type.model';
import { DEFAULT_PAGE_SIZE, GridUtility } from '../../grid-utility';
import { AdminTemplateEditButtonComponent } from './admin-template-edit-button.component';
import { AdminTemplateSettingItem } from './admin-template-settings.model';
import { IMDLTemplate } from '../../../../tamalelibs/models/template-mdl.model';

@Component({
    selector: 'tam-admin-template-settings',
    templateUrl: './admin-template-settings.component.html',
    styleUrls: ['./admin-template-settings.component.scss']
})
export class AdminTemplateSettingsComponent implements OnInit, OnDestroy {

    /**
     * an array of column definitions.
     */
    columnDefs: Array<ColDef> = [];

    /**
     * The option settings for the inner grid.
     */
    gridOptions: GridOptions;

    /**
     * a flag indicating whether the spinner should be visible.
     */
    isReloading = false;

    /**
     * default to hide the no data indicator.
     */
    noDataStyle = 'none';

    /**
     * the data source of the grid.
     */
    rowData: Array<AdminTemplateSettingItem> = [];

    /**
     * the Grid API reference
     */
    private _gridApi: GridApi;

    /**
     * the subscription collection persisting for unsubscribing them on destroying
     */
    private _destroySubscriptions: Array<Subscription> = [];

    constructor(private _store: Store<AppState>) { }

    ngOnInit() {
        this._initMDLTemplates();

        this._destroySubscriptions.push(
            this._store.pipe(
                select(createSelector(fullEntityTypeSelector, fullEntryTypeSelector, allMDLTemplatesSelector,
                    (entityTypes: Array<EntityType>, entryTypes: Array<EntryType>, mdlTemplates: Map<string, IMDLTemplate>) => {
                        const ds: Array<AdminTemplateSettingItem> = [];
                        if (entityTypes) {
                            ds.push(...entityTypes.map(eTTY => new AdminTemplateSettingItem(eTTY.id, eTTY.name,
                                'entity', this._isMDLConfiguredOnWeb(eTTY.id, mdlTemplates))));
                        }

                        if (entryTypes) {
                            ds.push(...entryTypes.map(eTRY => new AdminTemplateSettingItem(eTRY.id, eTRY.name,
                                'entry', this._isMDLConfiguredOnWeb(eTRY.id, mdlTemplates))));
                        }
                        return ds;
                    }))
            ).subscribe(result => {
                this.rowData = result;
            })
        );

        this.columnDefs = this._generateColDefs();
        this.gridOptions = this._generateGridOpions();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    @HostListener('window:resize', ['$event'])
    onResize(e) {
        if (!this._gridApi) {
            return;
        }
        this._gridApi.sizeColumnsToFit();
    }

    onDisplayedColumnsChanged(event) {
        event.api.sizeColumnsToFit();
    }

    onGridReady(params) {
        this._gridApi = params.api;
    }

    private _generateColDefs(): Array<ColDef> {
        const isConfiguredOnWebFormatFunc = (params) => {
            return params && params.data && params.data.isChangedOnWeb ? 'Yes' : 'No';
        };
        const typeFormatFunc = (params) => {
            return params && params.data && params.data.type ? params.data.type[0].toUpperCase() + params.data.type.slice(1) : '';
        };

        return [
            {
                headerName: 'Name',
                field: 'name',
                colId: 'name',
                tooltipField: 'name',
                minWidth: 80,
                width: 350,
                sortable: true,
                comparator: GridUtility.stringComparerFunc,
                menuTabs: [],
                cellStyle: GridUtility.cellStyleFunc
            },
            {
                headerName: 'Type',
                field: 'type',
                colId: 'type',
                valueGetter: typeFormatFunc,
                tooltipValueGetter: typeFormatFunc,
                tooltipField: 'type',
                minWidth: 50,
                width: 300,
                sortable: true,
                menuTabs: [],
                cellStyle: GridUtility.cellStyleFunc
            },
            {
                headerName: 'Configured on web',
                valueGetter: isConfiguredOnWebFormatFunc,
                tooltipValueGetter: isConfiguredOnWebFormatFunc,
                field: 'isChangedOnWeb',
                minWidth: 50,
                width: 300,
                sortable: true,
                menuTabs: [],
                cellStyle: GridUtility.cellStyleFunc
            },
            {
                headerName: 'Edit',
                minWidth: 96,
                width: 96,
                maxWidth: 96,
                cellRenderer: AdminTemplateEditButtonComponent,
                sortable: false,
                suppressMovable: true,
                menuTabs: [],
                headerClass: 'header-button',
            }
        ];
    }

    private _generateGridOpions(): GridOptions {
        return {
            defaultColDef: {
                sortable: true,
                resizable: true
            },
            suppressAutoSize: false,
            cacheBlockSize: 50,
            colResizeDefault: 'shift',
            suppressCellFocus: true,
            enableBrowserTooltips: true,
            paginationPageSize: DEFAULT_PAGE_SIZE,
            rowSelection: 'single',
            sortingOrder: ['desc', 'asc'],
            maintainColumnOrder: true,
            suppressContextMenu: true,
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns:true,
            getRowHeight: () => 40
        };
    }

    /**
* Determine whether the given template has been configured on Web.
* The logic is based on the contract below returned by server side:
*
* {
"7748406d739140c0823601393e747171": {  // entry type id
"75342a5ae4b44400b99397c74cff6972": { //control id
    "mdlProvider": {
        "fieldname": "ENTITIES",
        "mdlsource": "web",
        "mdlfieldname": "age",
        "providername": "Test001"
    }
},
"name": "Test2",  //entry type name
"c97582710a0f4dd3a2e08eb47be6d0d7": {
    "mdlProvider": {
        "fieldname": "ENTITIES",
        "mdlsource": "web",
        "mdlfieldname": "age",
        "providername": "Test002"
    }
}
}
*
* @param id the id of the given template(entity/entry type)
* @param mdlTemplates the existing MDL template configurations
* @returns a flag indicating whether the given template has been configured on Web
*/
    private _isMDLConfiguredOnWeb(id: string, mdlTemplates: Map<string, IMDLTemplate>): boolean {
        let isConfiguredOnWeb = false;
        if (mdlTemplates && 'has' in mdlTemplates) {
            if (mdlTemplates.has(id)) {// found the configuration for the given template id
                const template = mdlTemplates.get(id);
                template.mdlTemplate.forEach((value, key, map) => {
                    if (value.mdlProvider.mdlsource && value.mdlProvider.mdlsource === 'web') {
                        isConfiguredOnWeb = true;
                        return;
                    }
                });
            }
        }

        return isConfiguredOnWeb;
    }

    private _initMDLTemplates() {
        // initialize the MDL configurations
        this._store.dispatch({
            type: MDLTemplateActionTypes.INITIALIZE
        });
    }

}
